/* ============================================================
   PRAXIS — <praxis-sort>
   Custom, branded sort dropdown for the collection/search facet bar.
   Progressive enhancement over Dawn's native sort <select>: the select
   stays in the form (visually hidden) so facets.js keeps handling the
   AJAX re-sort — this element just proxies clicks to it by setting the
   value and firing the `input` event the form already listens for.
   Server-rendered in facets.liquid, so it survives Dawn's AJAX
   re-render (renderAdditionalElements swaps the .sorting innerHTML) and
   simply re-upgrades.
   ============================================================ */
(function () {
  if (customElements.get('praxis-sort')) return;

  // Set when a sort choice triggers Dawn's facet AJAX (which replaces the whole
  // <praxis-sort> via renderAdditionalElements); the fresh instance consumes it
  // so keyboard users don't drop focus to <body> after sorting.
  let refocusAfterRender = false;

  class PraxisSort extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this.trigger = this.querySelector('.praxis-sort__trigger');
      this.panel = this.querySelector('.praxis-sort__panel');
      this.native = this.querySelector('select[name="sort_by"]');
      this.currentEl = this.querySelector('[data-praxis-sort-current]');
      // Only latch _init once we have a well-formed element — so a malformed
      // render can still initialize on a later re-connect.
      if (!this.trigger || !this.panel || !this.native) return;
      this._init = true;

      if (refocusAfterRender) {
        refocusAfterRender = false;
        this.trigger.focus();
      }

      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
      this.panel.addEventListener('click', (e) => {
        const opt = e.target.closest('.praxis-sort__option');
        if (opt) {
          e.preventDefault();
          this.choose(opt);
        }
      });
      this.addEventListener('keydown', (e) => this.onKeydown(e));
    }

    get options() {
      return Array.from(this.panel.querySelectorAll('.praxis-sort__option'));
    }

    toggle(force) {
      const open = force === undefined ? !this.classList.contains('is-open') : force;
      this.classList.toggle('is-open', open);
      this.panel.hidden = !open;
      this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        PraxisSort.closeOthers(this);
        const active = this.panel.querySelector('.praxis-sort__option.is-active') || this.options[0];
        if (active) active.focus();
      }
    }

    close() {
      this.toggle(false);
    }

    choose(opt) {
      const value = opt.dataset.value;
      this.options.forEach((o) => {
        o.classList.remove('is-active');
        o.setAttribute('aria-selected', 'false');
      });
      opt.classList.add('is-active');
      opt.setAttribute('aria-selected', 'true');
      if (this.currentEl) this.currentEl.textContent = opt.textContent.trim();

      if (this.native.value !== value) {
        this.native.value = value;
        // Dawn's facet-filters-form listens for `input` on the form. The AJAX
        // re-render will replace this whole element — flag the fresh instance
        // to restore keyboard focus to its trigger.
        refocusAfterRender = true;
        this.native.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.close();
      this.trigger.focus();
    }

    onKeydown(e) {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
        this.trigger.focus();
        return;
      }
      if (!this.classList.contains('is-open')) return;
      const opts = this.options;
      const i = opts.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        (opts[i + 1] || opts[0]).focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        (opts[i - 1] || opts[opts.length - 1]).focus();
      }
    }

    static closeOthers(except) {
      document.querySelectorAll('praxis-sort.is-open').forEach((el) => {
        if (el !== except) el.close();
      });
    }
  }

  customElements.define('praxis-sort', PraxisSort);

  // Close on outside click.
  document.addEventListener('click', (e) => {
    if (e.target.closest('praxis-sort')) return;
    document.querySelectorAll('praxis-sort.is-open').forEach((el) => el.close());
  });
})();
